import { Content, ContentRelationshipField } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { TestimonialDocument } from '../../../prismicio-types';
import { StarIcon } from '@/assets';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { Carousel } from '@/components';

/**
 * Props for `TestimoniesCarousel`.
 */
export type TestimoniesCarouselProps =
  SliceComponentProps<Content.TestimoniesCarouselSlice>;

/**
 * Component for "TestimoniesCarousel" Slices.
 */
const TestimoniesCarousel = ({
  slice,
}: TestimoniesCarouselProps): JSX.Element => {
  const {
    primary: {
      testimonials,
      button_label: buttonLabel,
      button_link: buttonLink,
    },
  } = slice;
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12"
    >
      <Carousel dotExtraClass="bottom-1" autoPlaySpeed={8000}>
        {testimonials.map(({ testimonial }, index) => (
          <div key={`testimonial-${index}`}>
            <Testimonial
              testimonial={
                testimonial as ContentRelationshipField<'testimonial'> &
                  TestimonialDocument
              }
            />
          </div>
        ))}
      </Carousel>
      {buttonLabel && (
        <div className="flex justify-center mt-12">
          <PrismicNextLink field={buttonLink} className="button-bordered">
            <span>{buttonLabel}</span>
          </PrismicNextLink>
        </div>
      )}
    </section>
  );
};

const Testimonial = ({
  testimonial,
}: {
  testimonial: ContentRelationshipField<'testimonial'> & TestimonialDocument;
}) => {
  const {
    data: { quote, company_logo, avatar, name, job_title: jobTitle },
  } = testimonial;
  const starsArray = Array(5).fill(null);
  return (
    <div className="max-w-screen-2xl mx-auto flex flex-col items-center text-white pb-14">
      <div className="flex gap-x-1">
        {starsArray.map((_, index) => (
          <StarIcon key={`star-${index}`} />
        ))}
      </div>
      <div className="w-full md:w-2/3 lg:w-1/2 flex flex-col items-center">
        <PrismicRichText
          field={quote}
          components={{
            paragraph: ({ children }) => (
              <p className="text-xl leading-7 md:text-2xl md:leading-[33px] font-bold text-center m-6">
                {children}
              </p>
            ),
          }}
        />
        <div className="flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
          <PrismicNextImage field={avatar} />
          <div>
            <PrismicRichText
              field={name}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-base font-semibold">{children}</p>
                ),
              }}
            />
            <PrismicRichText
              field={jobTitle}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-base font-semibold">{children}</p>
                ),
              }}
            />
          </div>
          <figure className="hidden md:block bg-white w-[1px] h-[61px]" />
          <PrismicNextImage field={company_logo} className="max-w-40" />
        </div>
      </div>
    </div>
  );
};

export default TestimoniesCarousel;
